import React, { FC, ReactNode } from 'react';
import { classes, stylesheet } from 'typestyle';
import { DocType } from '../../../../../types';

import InfoIcon from '../../../../../images/components/InfoIcon';
import { Gray100, Gray50, typeCompact12Medium, White } from '@traceair/tds';

type InfoHintProps = {
  docType: DocType;
};

const InfoHint: FC<InfoHintProps> = ({ docType }) => {
  return (
    <div className={css.root} onClick={(e) => e.stopPropagation()}>
      <InfoIcon />
      <div className={classes('hint-container', css.hint)}>
        <Content docType={docType} />
      </div>
      <div className={classes('pipka', css.pipka)}>
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="4" viewBox="0 0 12 4" fill="none">
          <path
            d="M0 0H12C10.7194 0 9.49129 0.508707 8.58579 1.41421L6 4L3.41421 1.41421C2.50871 0.508708 1.28058 0 0 0Z"
            fill="#7A7A82"
          />
        </svg>
      </div>
    </div>
  );
};

const Content: FC<{ docType: DocType }> = ({ docType }) => {
  switch (docType) {
    case DocType.REMOTE_PILOT_CERTIFICATE:
      return (
        <>
          <Paragraph
            title="What is Remote Pilot Certificate"
            text="This certificate demonstrates a knowledge of the regulations, operating requirements, and procedures for safely flying drones."
          />
          <Paragraph
            title="How to get"
            text={
              <>
                {"To obtain a certificate, It's needed to pass the FAA Aeronautical Knowledge Examination."}
                <br />
                <a
                  className={classes(typeCompact12Medium, css.link)}
                  href={'https://www.faa.gov/uas/commercial_operators/become_a_drone_pilot'}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {"More info on the FAA's website"}
                </a>
              </>
            }
          />
          <Paragraph title="Validity period" text="24 months" />
        </>
      );

    case DocType.CERTIFICATE_OF_INSURANCE:
      return (
        <>
          <Paragraph
            title="What is Certificate of Insurance"
            text="Document that verifies the existence of an insurance policy and provides details of coverage."
          />
          <Paragraph
            title="How to get"
            text="Purchase aviation insurance from aviation insurance providers (e.g. Skywatch, BWI Fly, etc.) Minimum liability coverage of $1 million per incident. Please purchase a policy for several months to reduce monthly costs and the time required to verify insurance."
          />
          <Paragraph title="Validity period" text="Depends on insurance plan" />
        </>
      );

    case DocType.CERTIFICATE_OF_AUTHORIZATION:
      return (
        <>
          <Paragraph
            title="What is Certificate of Authorization"
            text="Document that provides a legal means of operating within controlled airspace and limits operations to specific parameters"
          />
          <Paragraph
            title="How to get"
            text={
              <>
                Apply for a COA through{' '}
                <a
                  className={classes(typeCompact12Medium, css.link)}
                  href={'https://faadronezone-access.faa.gov/#/'}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  the FAA DroneZone portal
                </a>
                . Feel free to ask if TraceAir has already obtained a COA for a specific project or to advise in the
                process of obtaining a COA for a specific project.
              </>
            }
          />
          <Paragraph title="Validity period" text="Depends on project" />
        </>
      );

    case DocType.EQUIPMENT_LOAN_AGREEMENT:
      return (
        <>
          <Paragraph
            title="What is Equipment Loan Agreement"
            text="Document that defines the current equipment in your possession that has partial or full ownership by TraceAir."
          />
          <Paragraph
            title="How to get"
            text="ELA is issued to you by TraceAir after a text or verbal agreement to terms of the equipment loan."
          />
          <Paragraph title="Validity period" text="Generally 2 years though variable" />
        </>
      );

    case DocType.SERVICE_LEVEL_AGREEMENT:
      return (
        <>
          <Paragraph
            title="What is Service Level Agreement"
            text="Document that defines the base relationship and expectations for performing work for TraceAir."
          />
          <Paragraph
            title="How to get"
            text="The SLA contract is sent to you via DocuSign at the beginning of the pilot onboarding process."
          />
          <Paragraph title="Validity period" text="Generally 10 years (self-extending)" />
        </>
      );

    case DocType.TAX_FORM_W9:
      return (
        <>
          <Paragraph
            title="What is W9 Tax Form"
            text="Document that contains your Taxpayer Identification Number (TIN) or Social Security Number (SSN). This permits us to properly report payments TraceAir makes."
          />
          <Paragraph title="Validity period" text="Lifetime" />
        </>
      );

    case DocType.TAX_FORM_1099:
      return (
        <>
          <Paragraph
            title="What is 1099 Tax Form"
            text="Document contains a summary of the revenue that TraceAir paid to your business or you personally for a specific calendar year."
          />
          <Paragraph
            title="How to get"
            text="The 1099 Tax Form will be provided by TraceAir annually in the first couple months of the year. It will be required for you to report your income from TraceAir to your tax entities (e.g. State, Federal)."
          />
          <Paragraph title="Validity period" text="1 year" />
        </>
      );

    default:
      return null;
  }
};

const Paragraph: FC<{ title?: string; text?: ReactNode }> = ({ title, text }) => (
  <>
    {title && <div className={classes(typeCompact12Medium, css.title)}>{title}</div>}
    {text && <div className={classes(typeCompact12Medium, css.text)}>{text}</div>}
  </>
);

const css = stylesheet({
  root: {
    cursor: 'default',
    position: 'relative',
    $nest: {
      '&:hover .hint-container': {
        display: 'block',
      },
      '&:hover .pipka': {
        display: 'block',
      },
    },
  },
  hint: {
    display: 'none',
    position: 'absolute',
    top: -7,
    left: '50%',
    transform: 'translate(-50%, -100%)',
    width: 200,
    boxSizing: 'border-box',
    padding: 8,
    borderRadius: 8,
    color: White,
    backgroundColor: Gray100,
  },
  pipka: {
    display: 'none',
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translate(-50%, -100%)',
  },
  link: {
    margin: 0,
    $nest: {
      '&:visited': {
        color: White,
      },
      '&:link': {
        color: White,
      },
      '&:hover': {
        color: White,
      },
      '&:active': {
        color: White,
      },
    },
  },
  title: {
    marginTop: 16,
    color: Gray50,
    $nest: {
      '&:first-child': {
        marginTop: 0,
      },
    },
  },
  text: {
    marginTop: 2,
  },
});

export default InfoHint;
